/**
 * One status change in the Etusivu "Viimeisin toiminta" feed, written as a
 * sentence: who did what to which tehtävä.
 *
 * Only status changes are recorded (`task_status_events`), so every sentence
 * is a status verb. Sync-driven changes carry a label such as
 * `Congrid-synkronointi` as the actor instead of a user.
 *
 * Subtasks carry almost no context of their own, so hanke and hankintapaketti
 * are read from the parent task when the event is about a subtask; clicking
 * such a row opens the parent.
 */

import type { TaskStatusEvent } from "@/hooks/useTaskStatusEvents";
import type { TaskExtended } from "@/types";
import { TaskStatus } from "@/types";
import { formatRelativeTimeFi } from "@/lib/format";
import { TASK_STATUS_LABELS_SINGULAR } from "@/lib/taskStatusMeta";
import { ProjectTag, PackageTag } from "./DashboardTags";

const STATUS_VERBS: Record<TaskStatus, string> = {
  [TaskStatus.COMPLETED]: "merkitsi valmiiksi tehtävän",
  [TaskStatus.IN_PROGRESS]: "aloitti tehtävän",
  [TaskStatus.PENDING]: "palautti aloittamattomaksi tehtävän",
  [TaskStatus.PROBLEM]: "merkitsi odottamaan aliurakoitsijaa tehtävän",
  [TaskStatus.REJECTED]: "hylkäsi tehtävän",
  [TaskStatus.CANNOT_MEASURE]: "merkitsi ei tarkastettavissa -tilaan tehtävän",
  [TaskStatus.NOT_APPLICABLE]: "merkitsi ei asiaankuuluvaksi tehtävän",
  [TaskStatus.UNKNOWN]: "muutti tilaa tehtävässä",
};

function actorName(event: TaskStatusEvent): string {
  if (event.actor) {
    const full = `${event.actor.firstName ?? ""} ${event.actor.lastName ?? ""}`.trim();
    if (full) return full;
    if (event.actor.email) return event.actor.email;
  }
  return event.actorLabel ?? "";
}

export function ActivityFeedRow({
  event,
  onClick,
}: {
  event: TaskStatusEvent;
  onClick: (task: TaskExtended) => void;
}) {
  const task = event.task;
  if (!task) return null;

  const context = task.parentId && event.parentTask ? event.parentTask : task;
  const pp =
    context.procurementPackage ?? context.section?.procurementPackage ?? null;
  const projectName = context.project?.name ?? "";
  const actor = actorName(event);
  const verb = STATUS_VERBS[event.newStatus] ?? "muutti tilaa tehtävässä";

  return (
    <button
      type="button"
      onClick={() => onClick(context)}
      className="flex w-full items-start gap-3 px-2 py-3 text-left transition-colors hover:bg-muted/40"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-5">
          {actor ? (
            <>
              <span className="font-medium text-foreground">{actor}</span>{" "}
              <span className="text-muted-foreground">{verb}</span>{" "}
              <span className="text-foreground">{task.title}</span>
            </>
          ) : (
            <>
              <span className="text-foreground">{task.title}</span>{" "}
              <span className="text-muted-foreground">
                → {TASK_STATUS_LABELS_SINGULAR[event.newStatus] ?? event.newStatus}
              </span>
            </>
          )}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <PackageTag code={pp?.code} name={pp?.name} />
          {projectName && <ProjectTag name={projectName} />}
        </div>
      </div>
      <span className="shrink-0 text-xs text-muted-foreground">
        {formatRelativeTimeFi(event.createdAt)}
      </span>
    </button>
  );
}
